import { m } from "framer-motion";
import { useLanguage } from "@/contexts/LanguageContext";
import { Crown, Infinity as InfinityIcon, Home, FolderPlus, BarChart3, MessageCircle, LayoutGrid } from "lucide-react";
import { Link } from "react-router-dom";

const PremiumSection = () => {
  const { t } = useLanguage();

  const premiumFeatures = [
    {
      icon: InfinityIcon,
      title: t("premium.unlimited.title"),
      description: t("premium.unlimited.desc"),
    },
    {
      icon: Home,
      title: t("premium.households.title"),
      description: t("premium.households.desc"),
    },
    {
      icon: FolderPlus,
      title: t("premium.custom.title"),
      description: t("premium.custom.desc"),
    },
    {
      icon: BarChart3,
      title: t("premium.stats.title"),
      description: t("premium.stats.desc"),
    },
    {
      icon: MessageCircle,
      title: t("premium.aichat.title"),
      description: t("premium.aichat.desc"),
    },
    {
      icon: LayoutGrid,
      title: t("premium.widgets.title"),
      description: t("premium.widgets.desc"),
    },
  ];

  return (
    <section id="premium" className="py-20 md:py-32 relative overflow-hidden">
      {/* Elegant gradient background with gold */}
      <div className="absolute inset-0 bg-gradient-to-br from-premium/5 via-background to-premium/10" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-primary/5 rounded-full blur-3xl" />
      
      <div className="container relative z-10">
        <m.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="relative"
        >
          {/* Premium card with elegant gold border */}
          <div className="relative rounded-3xl bg-gradient-to-br from-premium/10 via-background to-premium/5 border border-premium/20 p-8 md:p-12 shadow-xl">
            {/* Crown badge */}
            <div className="absolute -top-5 left-1/2 -translate-x-1/2">
              <Link to="/premium/features" className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-gradient-to-r from-premium to-premium/80 text-charcoal shadow-lg hover:from-premium/80 hover:to-premium hover:text-background transition-all">
                <Crown className="w-5 h-5" />
                <span className="font-semibold">{t("premium.badge")}</span>
              </Link>
            </div>

            <div className="text-center mt-6 mb-10">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                {t("premium.title")}
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
                {t("premium.subtitle")}
              </p>
            </div>

            {/* Features grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
              {premiumFeatures.map((feature, index) => (
                <m.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.1 * index }}
                  className="group p-6 rounded-2xl bg-background/60 border border-border/50 hover:border-premium/30 hover:shadow-lg transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-xl bg-premium/10 flex items-center justify-center mb-4 group-hover:bg-premium/20 transition-colors">
                    <feature.icon className="w-6 h-6 text-premium" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.description}</p>
                </m.div>
              ))}
            </div>

            {/* CTA Note */}
            <div className="text-center">
              <p className="text-sm text-muted-foreground">
                {t("premium.ctaNote")}
              </p>
            </div>
          </div>
        </m.div>
      </div>
    </section>
  );
};

export default PremiumSection;
